/**
 * EXP-01D — Find Events Like Mine
 *
 * 3-step guided matcher. Uses the deterministic EXP-01A matching engine.
 * Route: /find-events-like-mine
 * Public — no authentication required.
 *
 * Step 1 → What are you planning?   (event type, required)
 * Step 2 → What feels right?        (style / guests / setting / help, all optional)
 * Step 3 → Your closest matches     (top 3 + show 2 more)
 *
 * Design contract:
 *   - NEVER show match_score or percentages ("87% Match") to customers.
 *   - Input depth < 1 Step-2 answer → heading "Good places to start"
 *   - Input depth ≥ 1 Step-2 answer → heading "Your closest matches"
 *   - "Plan One Like This" stays disabled until EXP-01E ships.
 */

import { useState, useCallback, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Heart, Music, Gem, Gift, Briefcase, Monitor, Award, ArrowRight, ArrowLeft, Check, ChevronDown } from "lucide-react";
import { PJ } from "@/journey/journeyConfig";
import { loadJourneyFromStorage, saveJourneyToStorage, addJourneyPathEntry } from "@/journey/journeyEngine";

const API = process.env.REACT_APP_BACKEND_URL;

// ── Typography helpers ─────────────────────────────────────────────────────────
const serif = (extra = {}) => ({ fontFamily: "'Cormorant Garamond', Georgia, serif", ...extra });
const sans  = (extra = {}) => ({ fontFamily: "'DM Sans', sans-serif", ...extra });

// ── Step 1: event type options ─────────────────────────────────────────────────
const EVENT_TYPES = [
  { value: "WEDDING",    label: "Wedding",    Icon: Heart },
  { value: "SANGEET",    label: "Sangeet",    Icon: Music },
  { value: "ENGAGEMENT", label: "Engagement", Icon: Gem },
  { value: "BIRTHDAY",   label: "Birthday",   Icon: Gift },
  { value: "CORPORATE",  label: "Corporate",  Icon: Briefcase },
  { value: "CONFERENCE", label: "Conference", Icon: Monitor },
  { value: "AWARDS",     label: "Awards",     Icon: Award },
];

// ── Step 2: conversational dimensions ─────────────────────────────────────────

const STYLE_OPTIONS = [
  "Elegant", "Traditional", "Royal", "Modern",
  "Minimal", "Vibrant", "Grand", "Intimate",
];

const GUEST_OPTIONS = [
  { label: "Under 100",   value: "50_150" },
  { label: "100 – 250",   value: "150_300" },
  { label: "250 – 500",   value: "300_500" },
  { label: "500 – 750",   value: "500_750" },
  { label: "750 – 1,000", value: "750_PLUS" },
  { label: "1,000+",      value: "750_PLUS" },
  { label: "Not sure yet", value: null },
];

const SETTING_OPTIONS = [
  { label: "Hotel",           value: "INDOOR" },
  { label: "Convention Hall", value: "INDOOR" },
  { label: "Resort",          value: "BOTH" },
  { label: "Outdoor",         value: "OUTDOOR" },
  { label: "Home",            value: "BOTH" },
  { label: "Destination",     value: "BOTH" },
  { label: "Not sure yet",    value: null },
];

const HELP_OPTIONS = [
  { label: "Everything",        sub: "Full planning end-to-end",    value: "END_TO_END" },
  { label: "Selected services", sub: "Specific vendors or tasks",   value: "PARTIAL" },
  { label: "Just inspiration",  sub: "Ideas and direction only",    value: "CONSULTATION" },
  { label: "Not sure yet",      sub: "",                            value: null },
];

const EVENT_TYPE_LABELS = {
  WEDDING: "Wedding", SANGEET: "Sangeet", ENGAGEMENT: "Engagement",
  BIRTHDAY: "Birthday", CORPORATE: "Corporate", CONFERENCE: "Conference", AWARDS: "Awards",
};

// ── EXP-01E: Map transferable inspiration fields → BME journey field format ───
// Values are tagged INSPIRATION_SUGGESTED (confirmed=false).
// Customer confirms each field by advancing through the BME step.
// Never overrides CUSTOMER_CONFIRMED fields — checked by the caller.

const BME_EVENT_TYPES_VALID = new Set(['WEDDING','ENGAGEMENT','BIRTHDAY','CORPORATE','CONFERENCE','PRODUCT_LAUNCH','AWARDS','OTHER']);
const BME_ET_LABELS = { WEDDING:'Wedding', ENGAGEMENT:'Engagement', BIRTHDAY:'Birthday', CORPORATE:'Corporate Event', CONFERENCE:'Conference', PRODUCT_LAUNCH:'Product Launch', AWARDS:'Award Ceremony', OTHER:'Something Else' };

const INSP_SCOPE_TO_BME  = { END_TO_END:'END_TO_END', PARTIAL:'CUSTOM', CONSULTATION:'CUSTOM' };

// Inspiration style tags (display labels) → BME STYLE_OPTIONS values
const INSP_STYLE_TO_BME  = {
  Elegant:'ELEGANT', Traditional:'TRADITIONAL', Floral:'FLORAL',
  Royal:'LUXURY', Grand:'LUXURY', Luxury:'LUXURY', Glamorous:'LUXURY',
  Modern:'CONTEMPORARY', Vibrant:'VIBRANT', Minimal:'MINIMAL',
};

// Inspiration service display labels → BME SERVICES enum values
const INSP_SVC_TO_BME = {
  'Venue':'VENUE', 'Decor & Styling':'DECOR', 'Catering':'CATERING',
  'Photography':'PHOTOGRAPHY', 'Entertainment':'ENTERTAINMENT',
  'AV & Production':'AV_PRODUCTION', 'Logistics':'TRANSPORT',
  'Guest Logistics':'GUEST_HOSPITALITY', 'Floristry':'DECOR',
};

// Inspiration guest_range → BME GUEST_RANGES (approximate — customer can adjust)
const INSP_GUEST_TO_BME = {
  '50_150':  { v:'UNDER_100', d:'Under 100'   },
  '150_300': { v:'100_250',   d:'100 – 250'   },
  '300_500': { v:'250_500',   d:'250 – 500'   },
  '500_750': { v:'500_750',   d:'500 – 750'   },
  '750_PLUS':{ v:'750_1000',  d:'750 – 1,000' },
};

function mapInspirationToJourneyFields(fields) {
  const result = {};

  // event_type (SANGEET has no BME equivalent — skipped cleanly)
  if (fields.event_type && BME_EVENT_TYPES_VALID.has(fields.event_type)) {
    result.event_type = { value: fields.event_type, label: BME_ET_LABELS[fields.event_type], source: 'INSPIRATION_SUGGESTED', confirmed: false };
  }

  // planning_scope
  const bmeScope = INSP_SCOPE_TO_BME[fields.planning_scope];
  if (bmeScope) result.planning_scope = { value: bmeScope, source: 'INSPIRATION_SUGGESTED', confirmed: false };

  // style_tags (inspiration label strings → BME enum values, deduplicated)
  if (fields.style_tags?.length > 0) {
    const mapped = [...new Set((fields.style_tags).map(t => INSP_STYLE_TO_BME[t]).filter(Boolean))];
    if (mapped.length > 0) result.style_preferences = { value: mapped, source: 'INSPIRATION_SUGGESTED', confirmed: false };
  }

  // services (inspiration label strings → BME enum values, filtered to known)
  if (fields.services?.length > 0) {
    const valid = [...new Set((fields.services).map(s => INSP_SVC_TO_BME[s]).filter(Boolean))];
    if (valid.length > 0) result.service_interests = { value: valid, source: 'INSPIRATION_SUGGESTED', confirmed: false };
  }

  // guest_range (approximate mapping — customer can refine)
  const gm = INSP_GUEST_TO_BME[fields.guest_range];
  if (gm) result.guest_count_range = { value: gm.v, display: gm.d, source: 'INSPIRATION_SUGGESTED', confirmed: false };

  return result;
}

// ── Global styles ──────────────────────────────────────────────────────────────
const findStyles = `
  @keyframes findFadeIn { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; } }
  .find-fade { animation: findFadeIn 0.3s ease both; }
  .find-type-tile:hover { transform: translateY(-2px); }
  .find-pill:hover { border-color: ${PJ.gold} !important; }
`;

// ── Shared nav ─────────────────────────────────────────────────────────────────
function FindNav() {
  const nav = useNavigate();
  return (
    <nav
      data-testid="find-nav"
      style={{
        background: "rgba(32,27,25,0.97)", backdropFilter: "blur(12px)",
        position: "sticky", top: 0, zIndex: 100,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "0 clamp(16px,4vw,56px)", height: 60,
        borderBottom: "1px solid rgba(255,255,255,0.07)",
      }}
    >
      <button onClick={() => nav("/")} style={{ background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 10 }}>
        <img
          src="/prashasta-logo.png"
          alt="Prashasta Events"
          data-testid="find-nav-logo"
          style={{ height: 28, width: "auto", objectFit: "contain" }}
          onError={e => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "flex"; }}
        />
        <div style={{ display: "none", width: 28, height: 28, borderRadius: "50%", background: PJ.ruby, alignItems: "center", justifyContent: "center" }}>
          <span style={serif({ color: PJ.ivory, fontSize: 14, fontWeight: 600 })}>P</span>
        </div>
        <span style={serif({ color: PJ.ivory, fontSize: 18, fontWeight: 500 })}>Prashasta</span>
      </button>
      <div style={{ display: "flex", gap: 24, alignItems: "center" }}>
        <button onClick={() => nav("/explore")} style={{ background: "none", border: "none", cursor: "pointer", ...sans({ fontSize: 13, color: PJ.slate }) }}>
          Explore
        </button>
        <button onClick={() => nav("/build-my-event")} data-testid="find-nav-build"
          style={{ ...sans({ fontSize: 13, fontWeight: 600, color: PJ.white }), background: PJ.ruby, border: "none", borderRadius: 6, padding: "8px 16px", cursor: "pointer" }}>
          Build My Event
        </button>
      </div>
    </nav>
  );
}

// ── Progress dots ──────────────────────────────────────────────────────────────
function ProgressDots({ step }) {
  return (
    <div data-testid="find-progress-dots" style={{ display: "flex", gap: 8, justifyContent: "center", padding: "28px 0 0" }}>
      {[1, 2, 3].map(i => (
        <div key={i} style={{
          width: i === step ? 20 : 6,
          height: 6, borderRadius: 3,
          background: i === step ? PJ.gold : i < step ? PJ.gold + "66" : "rgba(255,255,255,0.18)",
          transition: "all 0.3s ease",
        }} />
      ))}
    </div>
  );
}

// ── Step 1: What are you planning? ─────────────────────────────────────────────
function Step1({ onSelect }) {
  return (
    <div className="find-fade" data-testid="find-step-1"
      style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", padding: "40px clamp(16px,4vw,56px) 64px" }}>
      <p style={sans({ fontSize: "0.65rem", fontWeight: 600, letterSpacing: "0.20em", textTransform: "uppercase", color: PJ.gold, margin: "0 0 14px" })}>
        Step 1 of 3
      </p>
      <h1 style={serif({ fontSize: "clamp(2rem,4.5vw,3.2rem)", fontWeight: 400, color: PJ.ivory, margin: "0 0 12px", lineHeight: 1.1, textAlign: "center" })}>
        What are you planning?
      </h1>
      <p style={sans({ fontSize: "0.9rem", color: PJ.slate, margin: "0 0 48px", textAlign: "center", maxWidth: 380, lineHeight: 1.6 })}>
        Tell us the kind of event and we'll find inspiration that fits.
      </p>

      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 140px), 1fr))",
        gap: 12,
        width: "100%",
        maxWidth: 680,
      }}>
        {EVENT_TYPES.map(({ value, label, Icon }) => (
          <button
            key={value}
            data-testid={`find-type-${value.toLowerCase()}`}
            className="find-type-tile"
            onClick={() => onSelect(value)}
            style={{
              background: "rgba(255,255,255,0.05)", border: "1.5px solid rgba(255,255,255,0.12)",
              borderRadius: 12, padding: "20px 16px 18px", cursor: "pointer",
              display: "flex", flexDirection: "column", alignItems: "center", gap: 10,
              transition: "all 0.2s",
            }}
            onMouseEnter={e => { e.currentTarget.style.background = "rgba(201,161,95,0.12)"; e.currentTarget.style.borderColor = PJ.gold; }}
            onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.05)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.12)"; }}
          >
            <Icon size={22} style={{ color: PJ.gold }} />
            <span style={sans({ fontSize: "0.82rem", fontWeight: 600, color: PJ.ivory, letterSpacing: "0.02em" })}>{label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Pill option button ─────────────────────────────────────────────────────────
function PillOption({ label, sub, selected, onClick, testId }) {
  return (
    <button
      data-testid={testId}
      className="find-pill"
      onClick={onClick}
      style={{
        background: selected ? PJ.gold + "22" : "transparent",
        border: `1.5px solid ${selected ? PJ.gold : "rgba(32,27,25,0.20)"}`,
        borderRadius: 8, padding: sub ? "10px 16px" : "9px 16px",
        cursor: "pointer", textAlign: "left",
        transition: "border-color 0.15s, background 0.15s",
        display: "flex", flexDirection: "column", gap: 2,
      }}
    >
      <span style={sans({ fontSize: "0.875rem", fontWeight: selected ? 600 : 400, color: PJ.obsidian })}>{label}</span>
      {sub && <span style={sans({ fontSize: "0.72rem", color: PJ.slate })}>{sub}</span>}
    </button>
  );
}

// ── Step 2: What feels right? ──────────────────────────────────────────────────
function Step2({ answers, setAnswers, onBack, onSubmit }) {
  const toggleStyle = useCallback((tag) => {
    setAnswers(a => ({
      ...a,
      style_tags: a.style_tags.includes(tag)
        ? a.style_tags.filter(t => t !== tag)
        : [...a.style_tags, tag],
    }));
  }, [setAnswers]);

  const step2Answered = [answers.style_tags.length > 0, answers.guest_range !== null, answers.venue_setting !== null, answers.planning_scope !== null].some(Boolean);

  return (
    <div className="find-fade" data-testid="find-step-2"
      style={{ flex: 1, padding: "40px clamp(16px,4vw,56px) 64px", maxWidth: 720, width: "100%", margin: "0 auto" }}>
      <p style={sans({ fontSize: "0.65rem", fontWeight: 600, letterSpacing: "0.20em", textTransform: "uppercase", color: PJ.gold, margin: "0 0 14px" })}>
        Step 2 of 3
      </p>
      <h1 style={serif({ fontSize: "clamp(1.8rem,3.5vw,2.8rem)", fontWeight: 400, color: PJ.obsidian, margin: "0 0 6px", lineHeight: 1.1 })}>
        What feels right?
      </h1>
      <p style={sans({ fontSize: "0.875rem", color: PJ.slate, margin: "0 0 40px", lineHeight: 1.6 })}>
        Answer as many or as few as you like. Every question is optional.
      </p>

      {/* 1. Style / feel */}
      <div style={{ marginBottom: 36 }}>
        <p style={sans({ fontSize: "0.78rem", fontWeight: 600, color: PJ.obsidian, margin: "0 0 14px" })}>What should it feel like?</p>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {STYLE_OPTIONS.map(tag => {
            const sel = answers.style_tags.includes(tag);
            return (
              <button
                key={tag}
                data-testid={`find-style-${tag.toLowerCase()}`}
                onClick={() => toggleStyle(tag)}
                style={{
                  background: sel ? PJ.gold + "22" : "transparent",
                  border: `1.5px solid ${sel ? PJ.gold : "rgba(32,27,25,0.22)"}`,
                  borderRadius: 20, padding: "7px 16px", cursor: "pointer",
                  ...sans({ fontSize: "0.85rem", fontWeight: sel ? 600 : 400, color: PJ.obsidian }),
                  transition: "all 0.15s",
                }}
              >
                {sel && <Check size={11} style={{ marginRight: 4, verticalAlign: "middle", color: PJ.gold }} />}
                {tag}
              </button>
            );
          })}
          <button
            data-testid="find-style-not-sure"
            onClick={() => setAnswers(a => ({ ...a, style_tags: [] }))}
            style={{
              background: answers.style_tags.length === 0 ? PJ.gold + "22" : "transparent",
              border: `1.5px solid ${answers.style_tags.length === 0 ? PJ.gold : "rgba(32,27,25,0.22)"}`,
              borderRadius: 20, padding: "7px 16px", cursor: "pointer",
              ...sans({ fontSize: "0.85rem", color: PJ.slate }),
              transition: "all 0.15s",
            }}
          >
            Not sure yet
          </button>
        </div>
      </div>

      {/* 2. Guest scale */}
      <div style={{ marginBottom: 36 }}>
        <p style={sans({ fontSize: "0.78rem", fontWeight: 600, color: PJ.obsidian, margin: "0 0 14px" })}>Roughly how many guests?</p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 8 }}>
          {GUEST_OPTIONS.map(({ label, value }) => (
            <PillOption
              key={label}
              testId={`find-guest-${label.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
              label={label}
              selected={answers.guest_range === value && value !== null ? true : (value === null && answers.guest_range === null && label === "Not sure yet")}
              onClick={() => setAnswers(a => ({ ...a, guest_range: value }))}
            />
          ))}
        </div>
      </div>

      {/* 3. Setting */}
      <div style={{ marginBottom: 36 }}>
        <p style={sans({ fontSize: "0.78rem", fontWeight: 600, color: PJ.obsidian, margin: "0 0 14px" })}>What kind of setting feels right?</p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 8 }}>
          {SETTING_OPTIONS.map(({ label, value }) => (
            <PillOption
              key={label}
              testId={`find-setting-${label.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
              label={label}
              selected={answers.venue_setting === value && value !== null ? true : (value === null && answers.venue_setting === null && label === "Not sure yet")}
              onClick={() => setAnswers(a => ({ ...a, venue_setting: value }))}
            />
          ))}
        </div>
      </div>

      {/* 4. Planning help */}
      <div style={{ marginBottom: 48 }}>
        <p style={sans({ fontSize: "0.78rem", fontWeight: 600, color: PJ.obsidian, margin: "0 0 14px" })}>How much help would you like?</p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 8 }}>
          {HELP_OPTIONS.map(({ label, sub, value }) => (
            <PillOption
              key={label}
              testId={`find-help-${label.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
              label={label}
              sub={sub}
              selected={answers.planning_scope === value && value !== null ? true : (value === null && answers.planning_scope === null && label === "Not sure yet")}
              onClick={() => setAnswers(a => ({ ...a, planning_scope: value }))}
            />
          ))}
        </div>
      </div>

      {/* Actions */}
      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <button
          data-testid="find-step2-back"
          onClick={onBack}
          style={{ ...sans({ fontSize: 14, fontWeight: 500, color: PJ.slate }), background: "none", border: "none", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}
        >
          <ArrowLeft size={14} /> Back
        </button>
        <button
          data-testid="find-step2-submit"
          onClick={onSubmit}
          style={{
            ...sans({ fontSize: 14, fontWeight: 600, color: PJ.white }),
            background: PJ.ruby, border: "none", borderRadius: 8,
            padding: "12px 32px", cursor: "pointer",
            display: "inline-flex", alignItems: "center", gap: 8,
            transition: "opacity 0.15s",
          }}
          onMouseEnter={e => { e.currentTarget.style.opacity = "0.88"; }}
          onMouseLeave={e => { e.currentTarget.style.opacity = "1"; }}
        >
          See My Matches <ArrowRight size={14} />
        </button>
      </div>
    </div>
  );
}

// ── Gap panel (collapsed "A little different") ─────────────────────────────────
function GapPanel({ gaps }) {
  const [open, setOpen] = useState(false);
  if (!gaps || gaps.length === 0) return null;
  return (
    <div style={{ marginTop: 12 }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          background: "none", border: "none", cursor: "pointer",
          ...sans({ fontSize: "0.8rem", fontWeight: 500, color: PJ.slate }),
          display: "inline-flex", alignItems: "center", gap: 6,
          padding: 0,
        }}
        data-testid="find-gap-toggle"
      >
        <span style={{ transition: "transform 0.2s", display: "inline-block", transform: open ? "rotate(180deg)" : "none" }}>
          <ChevronDown size={14} />
        </span>
        A little different from your plan
      </button>
      {open && (
        <ul style={{ marginTop: 8, paddingLeft: 0, listStyle: "none" }}>
          {gaps.map((g, i) => (
            <li key={i} style={{ ...sans({ fontSize: "0.82rem", color: PJ.slate, lineHeight: 1.6 }), display: "flex", gap: 8, marginBottom: 4 }}>
              <span style={{ color: PJ.gold, marginTop: 1, flexShrink: 0 }}>·</span> {g}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Primary match card (editorial, first result) ───────────────────────────────
function PrimaryMatchCard({ match, onViewStory, onPlanLikeThis, isPlanning }) {
  const { event, match_reasons, match_gaps } = match;
  const fx = event.hero_focal_x ?? 50;
  const fy = event.hero_focal_y ?? 38;

  return (
    <div data-testid="find-primary-match" style={{
      background: "#FFFFFF", borderRadius: 16, overflow: "hidden",
      boxShadow: "0 8px 40px rgba(0,0,0,0.12)", marginBottom: 24,
    }}>
      {/* Large hero */}
      <div style={{ position: "relative", height: 280, overflow: "hidden", cursor: "pointer" }}
           onClick={() => onViewStory(event.slug)}>
        <img src={event.hero_image_url} alt={event.title} loading="lazy"
          style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: `${fx}% ${fy}%` }} />
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(32,27,25,0.5), transparent 55%)", pointerEvents: "none" }} />
        <span style={{
          position: "absolute", top: 14, left: 14,
          ...sans({ fontSize: "0.6rem", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: PJ.white }),
          background: PJ.gold, borderRadius: 4, padding: "4px 10px",
        }}>
          Best Match
        </span>
      </div>

      {/* Content */}
      <div style={{ padding: "24px 28px 28px" }}>
        <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
          <span style={{ ...sans({ fontSize: 10, fontWeight: 700, letterSpacing: "0.10em", textTransform: "uppercase" }), background: PJ.ruby, color: PJ.white, padding: "3px 8px", borderRadius: 3 }}>
            {EVENT_TYPE_LABELS[event.event_type] || event.event_type}
          </span>
          <span style={{ ...sans({ fontSize: 10, fontWeight: 500, color: PJ.slate }), background: PJ.ivory, border: `1px solid ${PJ.sand}`, padding: "3px 8px", borderRadius: 3 }}>
            {event.location_city}
          </span>
        </div>

        <h2 style={serif({ fontSize: "clamp(1.4rem,2.5vw,2rem)", fontWeight: 400, color: PJ.obsidian, margin: "0 0 4px", lineHeight: 1.15 })}>
          {event.title}
        </h2>
        <p style={sans({ fontSize: "0.82rem", color: PJ.slate, margin: "0 0 20px" })}>{event.style_direction}</p>

        {/* Why this fits */}
        {match_reasons.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <p style={sans({ fontSize: "0.65rem", fontWeight: 600, letterSpacing: "0.14em", textTransform: "uppercase", color: PJ.gold, margin: "0 0 8px" })}>
              Why this fits
            </p>
            {match_reasons.map((r, i) => (
              <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 5 }}>
                <Check size={13} style={{ color: PJ.gold, flexShrink: 0, marginTop: 2 }} />
                <span style={sans({ fontSize: "0.875rem", color: PJ.obsidian, lineHeight: 1.4 })}>{r}</span>
              </div>
            ))}
          </div>
        )}

        <GapPanel gaps={match_gaps} />

        {/* CTAs */}
        <div style={{ display: "flex", gap: 10, marginTop: 24, flexWrap: "wrap" }}>
          <button
            data-testid={`find-view-story-${event.slug}`}
            onClick={() => onViewStory(event.slug)}
            style={{
              ...sans({ fontSize: 13, fontWeight: 600, color: PJ.white }),
              background: PJ.obsidian, border: "none", borderRadius: 8,
              padding: "10px 20px", cursor: "pointer",
              display: "inline-flex", alignItems: "center", gap: 6,
              transition: "opacity 0.15s",
            }}
            onMouseEnter={e => { e.currentTarget.style.opacity = "0.85"; }}
            onMouseLeave={e => { e.currentTarget.style.opacity = "1"; }}
          >
            View Event Story <ArrowRight size={13} />
          </button>
          <button
            data-testid={`find-plan-like-this-${event.slug}`}
            onClick={() => onPlanLikeThis(event.slug, event.title)}
            disabled={isPlanning}
            style={{
              ...sans({ fontSize: 13, fontWeight: 600, color: isPlanning ? PJ.slate : PJ.white }),
              background: isPlanning ? "transparent" : PJ.ruby,
              border: `1.5px solid ${isPlanning ? PJ.sand : PJ.ruby}`,
              borderRadius: 8, padding: "10px 20px",
              cursor: isPlanning ? "wait" : "pointer",
              display: "inline-flex", alignItems: "center", gap: 6,
              transition: "all 0.15s",
            }}
            onMouseEnter={e => { if (!isPlanning) { e.currentTarget.style.opacity = "0.88"; } }}
            onMouseLeave={e => { e.currentTarget.style.opacity = "1"; }}
          >
            {isPlanning ? "Preparing your plan…" : <>Plan One Like This <ArrowRight size={13} /></>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Secondary match card ───────────────────────────────────────────────────────
function SecondaryMatchCard({ match, rank, onViewStory }) {
  const { event, match_reasons, match_gaps } = match;
  const fx   = event.hero_focal_x ?? 50;
  const fy   = event.hero_focal_y ?? 38;
  const [hov, setHov] = useState(false);

  return (
    <div
      data-testid={`find-secondary-match-${rank}`}
      style={{
        background: "#FFFFFF", borderRadius: 12, overflow: "hidden",
        boxShadow: hov ? "0 12px 32px rgba(0,0,0,0.12)" : "0 2px 12px rgba(0,0,0,0.07)",
        transform: hov ? "translateY(-2px)" : "none",
        transition: "all 0.2s",
        cursor: "pointer",
        display: "flex", flexDirection: "column",
      }}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      onClick={() => onViewStory(event.slug)}
    >
      <div style={{ height: 160, overflow: "hidden", flexShrink: 0 }}>
        <img src={event.hero_image_url} alt={event.title} loading="lazy"
          style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: `${fx}% ${fy}%`,
                   transform: hov ? "scale(1.04)" : "scale(1)", transition: "transform 0.35s" }} />
      </div>
      <div style={{ padding: "16px 18px 20px", flex: 1 }}>
        <span style={{ ...sans({ fontSize: 10, fontWeight: 700, letterSpacing: "0.10em", textTransform: "uppercase" }), background: PJ.ruby, color: PJ.white, padding: "3px 7px", borderRadius: 3, marginBottom: 10, display: "inline-block" }}>
          {EVENT_TYPE_LABELS[event.event_type] || event.event_type}
        </span>
        <h3 style={serif({ fontSize: "1.15rem", fontWeight: 400, color: PJ.obsidian, margin: "8px 0 4px", lineHeight: 1.2 })}>{event.title}</h3>
        <p style={sans({ fontSize: "0.78rem", color: PJ.slate, margin: "0 0 12px" })}>{event.location_city}</p>
        {match_reasons.slice(0, 2).map((r, i) => (
          <div key={i} style={{ display: "flex", gap: 6, alignItems: "flex-start", marginBottom: 4 }}>
            <Check size={11} style={{ color: PJ.gold, flexShrink: 0, marginTop: 2 }} />
            <span style={sans({ fontSize: "0.78rem", color: PJ.slate, lineHeight: 1.4 })}>{r}</span>
          </div>
        ))}
        <GapPanel gaps={match_gaps} />
      </div>
    </div>
  );
}

// ── Step 3: Results ────────────────────────────────────────────────────────────
function Step3({ answers, results, onRestart, onViewStory, onPlanLikeThis, isPlanning, showMore, setShowMore }) {
  const nav = useNavigate();
  const inputDepth = [
    answers.style_tags.length > 0,
    answers.guest_range !== null,
    answers.venue_setting !== null,
    answers.planning_scope !== null,
  ].filter(Boolean).length;

  const headingCopy = inputDepth === 0 ? "Good places to start" : "Your closest matches";
  const subCopy     = inputDepth === 0
    ? "Based on the event type you chose. Add more details to refine these results."
    : "Based on what you shared with us.";

  const visible = showMore ? results : results.slice(0, 3);
  const hasMore = results.length > 3 && !showMore;
  const [primary, ...secondary] = visible;

  return (
    <div className="find-fade" data-testid="find-step-3"
      style={{ flex: 1, padding: "40px clamp(16px,4vw,56px) 64px", maxWidth: 920, width: "100%", margin: "0 auto" }}>

      {/* Back */}
      <button
        data-testid="find-results-back"
        onClick={onRestart}
        style={{ background: "none", border: "none", cursor: "pointer", ...sans({ fontSize: 13, color: PJ.slate, marginBottom: 28 }), display: "flex", alignItems: "center", gap: 6 }}
      >
        <ArrowLeft size={13} /> Start over
      </button>

      {/* Heading */}
      <p style={sans({ fontSize: "0.65rem", fontWeight: 600, letterSpacing: "0.18em", textTransform: "uppercase", color: PJ.gold, margin: "0 0 8px" })}>
        Step 3 of 3
      </p>
      <h1 data-testid="find-results-heading" style={serif({ fontSize: "clamp(1.8rem,3.5vw,2.8rem)", fontWeight: 400, color: PJ.obsidian, margin: "0 0 8px", lineHeight: 1.1 })}>
        {headingCopy}
      </h1>
      <p style={sans({ fontSize: "0.875rem", color: PJ.slate, margin: "0 0 36px", lineHeight: 1.6 })}>
        {subCopy}
      </p>

      {results.length === 0 ? (
        <div data-testid="find-no-results" style={{ textAlign: "center", padding: "48px 24px", background: PJ.ivory, borderRadius: 16 }}>
          <p style={sans({ fontSize: "0.9rem", color: PJ.slate, marginBottom: 20 })}>
            No close matches found. Try different preferences or browse the full gallery.
          </p>
          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            <button onClick={onRestart}
              style={{ ...sans({ fontSize: 13, fontWeight: 500, color: PJ.slate }), background: "none", border: `1.5px solid ${PJ.sand}`, borderRadius: 8, padding: "10px 20px", cursor: "pointer" }}>
              Adjust Preferences
            </button>
            <button onClick={() => nav("/explore")}
              style={{ ...sans({ fontSize: 13, fontWeight: 600, color: PJ.white }), background: PJ.ruby, border: "none", borderRadius: 8, padding: "10px 24px", cursor: "pointer" }}>
              Browse All Events
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* YOUR CLOSEST MATCH label */}
          <p style={sans({ fontSize: "0.6rem", fontWeight: 700, letterSpacing: "0.18em", textTransform: "uppercase", color: PJ.slate, margin: "0 0 12px" })}>
            Your closest match
          </p>
          <PrimaryMatchCard match={primary} onViewStory={onViewStory} onPlanLikeThis={onPlanLikeThis} isPlanning={isPlanning} />

          {/* ALSO WORTH EXPLORING */}
          {secondary.length > 0 && (
            <>
              <p style={sans({ fontSize: "0.6rem", fontWeight: 700, letterSpacing: "0.18em", textTransform: "uppercase", color: PJ.slate, margin: "0 0 16px" })}>
                Also worth exploring
              </p>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 280px), 1fr))", gap: 16, marginBottom: 28 }}>
                {secondary.map((m, i) => (
                  <SecondaryMatchCard key={m.event.slug} match={m} rank={i + 2} onViewStory={onViewStory} />
                ))}
              </div>
            </>
          )}

          {/* Show 2 more */}
          {hasMore && (
            <div style={{ textAlign: "center", marginBottom: 32 }}>
              <button
                data-testid="find-show-more"
                onClick={() => setShowMore(true)}
                style={{ background: "none", border: `1.5px solid ${PJ.sand}`, borderRadius: 8, padding: "10px 24px", cursor: "pointer",
                         ...sans({ fontSize: 13, fontWeight: 500, color: PJ.slate }), transition: "border-color 0.15s" }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = PJ.gold; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = PJ.sand; }}
              >
                Show 2 more matches
              </button>
            </div>
          )}
        </>
      )}

      {/* CTA band */}
      <div style={{ background: PJ.obsidian, borderRadius: 16, padding: "36px 32px", textAlign: "center", marginTop: 16 }}>
        <p style={sans({ fontSize: "0.8rem", color: PJ.slate, margin: "0 0 12px", lineHeight: 1.6 })}>
          Not the right fit? Start building your event directly.
        </p>
        <button
          data-testid="find-results-build"
          onClick={() => nav("/build-my-event")}
          style={{ ...sans({ fontSize: 14, fontWeight: 600, color: PJ.white }), background: PJ.ruby, border: "none", borderRadius: 8, padding: "12px 28px", cursor: "pointer",
                   display: "inline-flex", alignItems: "center", gap: 8 }}>
          Build My Event <ArrowRight size={14} />
        </button>
      </div>
    </div>
  );
}

// ── Loading spinner ────────────────────────────────────────────────────────────
function FindLoading() {
  return (
    <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", minHeight: "50vh" }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ width: 36, height: 36, borderRadius: "50%", border: "2px solid rgba(32,27,25,0.10)", borderTopColor: PJ.gold, animation: "findFadeIn 0.8s linear infinite", margin: "0 auto 16px" }} />
        <p style={sans({ fontSize: "0.78rem", color: PJ.slate })}>Finding your matches…</p>
      </div>
    </div>
  );
}

// ── Page bg per step ───────────────────────────────────────────────────────────
const STEP_BG = { 1: PJ.obsidian, 2: PJ.ivory, 3: PJ.ivory };

// ── Main page ──────────────────────────────────────────────────────────────────
export default function FindEventsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [answers,  setAnswers]  = useState({ event_type: null, style_tags: [], guest_range: null, venue_setting: null, planning_scope: null });
  const [results,  setResults]  = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [planningInspiration, setPlanningInspiration] = useState(false);

  // Step is owned by the URL — browser Back/Forward work automatically
  const step = parseInt(searchParams.get("step") || "1", 10);

  // Guard: on refresh or direct link, required state may be missing
  useEffect(() => {
    if ((step === 2 || step === 3) && !answers.event_type) {
      // No event_type in component state = fresh load mid-flow; restart cleanly
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.set("step", "1");
        return next;
      }, { replace: true });
    }
  }); // intentionally no deps — runs every render as a safety check

  // EXP-01E bridge: merge event_match into the canonical prashasta_event_journey session.
  // Reuses the existing journey_session_id if one exists (e.g. user visited BME first).
  // Creates a minimal session with a new ID if this is the customer's first touchpoint.
  // NOTE: inspiration.selected_match_slug is set only in EXP-01E on explicit customer action.
  // EXP-01G: Sets matcher-origin source attribution on first event_type selection.
  // source_captured=true prevents BME's initJourneySource from overwriting the acquisition source.
  useEffect(() => {
    if (!answers.event_type) return;
    const existing = loadJourneyFromStorage();
    const sessionId = existing?.journey_session_id || crypto.randomUUID();

    // Matcher-origin source attribution: set once, never overwritten
    const matcherAttribution = existing?.source_captured ? {} : {
      source_channel:     'WEBSITE',
      source_touchpoint:  'EVENT_MATCH',
      source_entry_point: 'FIND_EVENTS_LIKE_MINE',
      source_captured:    true,
      source_first_seen:  new Date().toISOString(),
    };

    // FIND_EVENTS_LIKE_MINE path entry: honest record — customer is in the matcher
    const currentPath = existing?.journey_path || [];
    const path = currentPath.includes('FIND_EVENTS_LIKE_MINE')
      ? currentPath
      : [...currentPath, 'FIND_EVENTS_LIKE_MINE'];

    saveJourneyToStorage({
      ...(existing || {}),
      journey_session_id: sessionId,
      ...matcherAttribution,
      journey_path: path,
      event_match: {
        event_type:     answers.event_type,
        style_tags:     answers.style_tags,
        guest_range:    answers.guest_range,
        venue_setting:  answers.venue_setting,
        planning_scope: answers.planning_scope,
      },
    });
  }, [answers]);

  const handleTypeSelect = (type) => {
    setAnswers(a => ({ ...a, event_type: type }));
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set("step", "2");
      return next;
    });           // push → browser Back returns to Step 1
  };

  const handleSubmit = async () => {
    setLoading(true);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set("step", "3");
      return next;
    });           // push → browser Back returns to Step 2 with answers preserved
    try {
      const body = { event_type: answers.event_type };
      if (answers.style_tags.length > 0)  body.style_tags    = answers.style_tags;
      if (answers.guest_range)             body.guest_range    = answers.guest_range;
      if (answers.venue_setting)           body.venue_setting  = answers.venue_setting;
      if (answers.planning_scope)          body.planning_scope = answers.planning_scope;

      const r = await fetch(`${API}/api/public/inspirations/match`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await r.json();
      setResults(data.matches || []);
      // Honest path: customer has seen their match results
      const afterMatch = loadJourneyFromStorage();
      if (afterMatch) {
        saveJourneyToStorage({
          ...afterMatch,
          journey_path: addJourneyPathEntry(afterMatch, 'MATCH_RESULTS'),
        });
      }
    } catch (e) {
      console.error("FindEvents: match failed", e);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRestart = () => {
    setAnswers({ event_type: null, style_tags: [], guest_range: null, venue_setting: null, planning_scope: null });
    setResults(null);
    setShowMore(false);
    setLoading(false);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set("step", "1");
      return next;
    }, { replace: true }); // replace — Start Over clears history
    // Merge-remove event_match from journey session; preserve all other journey fields
    const existing = loadJourneyFromStorage();
    if (existing) {
      const { event_match, ...rest } = existing; // eslint-disable-line no-unused-vars
      saveJourneyToStorage(rest);
    }
  };

  // View an event story — navigation only.
  // EXP-01E sets inspiration.selected = true + selected_match_slug on explicit "Plan One Like This" action.
  // Viewing a story (even the Best Match) does NOT constitute a customer selection.
  // Honest path: adds EVENT_STORY only when customer actually navigates to a story.
  const handleViewStory = (slug) => {
    const existing = loadJourneyFromStorage();
    if (existing) {
      saveJourneyToStorage({
        ...existing,
        journey_path: addJourneyPathEntry(existing, 'EVENT_STORY'),
      });
    }
    navigate(`/explore/${slug}`);
  };

  // EXP-01E: Customer explicitly clicks "Plan One Like This" on a match card.
  // 1. Fetch whitelisted transferable fields from backend.
  // 2. Map to BME journey field format (INSPIRATION_SUGGESTED / confirmed=false).
  // 3. Only apply fields not already CUSTOMER_CONFIRMED by the customer.
  // 4. Record inspiration metadata in journey session.
  // 5. Navigate to Build My Event.
  const handlePlanLikeThis = useCallback(async (slug, eventTitle) => {
    setPlanningInspiration(true);
    try {
      const res = await fetch(`${API}/api/public/inspirations/${slug}/transferable`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      const allSuggested = mapInspirationToJourneyFields(data.fields || {});
      const existing = loadJourneyFromStorage();
      const sessionId = existing?.journey_session_id || crypto.randomUUID();

      // Never override a field already confirmed by the customer
      const fieldsToApply = {};
      Object.entries(allSuggested).forEach(([key, val]) => {
        if (existing?.[key]?.confirmed !== true) fieldsToApply[key] = val;
      });

      // Inspiration-origin source attribution: set only if no prior source captured
      // (matcher-origin customers keep their EVENT_MATCH touchpoint — never overwritten)
      const inspirationAttribution = existing?.source_captured ? {} : {
        source_channel:     'WEBSITE',
        source_touchpoint:  'INSPIRATION_GALLERY',
        source_entry_point: 'PLAN_ONE_LIKE_THIS',
        source_captured:    true,
        source_first_seen:  new Date().toISOString(),
      };

      // Honest path: PLAN_ONE_LIKE_THIS records the explicit customer action.
      // INSPIRATION_GALLERY is NOT added here — it would only be in the path if the
      // customer actually visited /explore (ExplorePage adds it independently).
      const pathAfterPlanning = addJourneyPathEntry(existing || {}, 'PLAN_ONE_LIKE_THIS');

      saveJourneyToStorage({
        ...(existing || {}),
        journey_session_id: sessionId,
        ...inspirationAttribution,
        journey_path: pathAfterPlanning,
        ...fieldsToApply,
        // inspiration.selected = true marks explicit customer choice (not just a best-match view)
        inspiration: {
          selected:            true,
          selected_match_slug: slug,
          inspiration_title:   data.inspiration_title || eventTitle,
          source:              'PORTFOLIO',
          selected_at:         new Date().toISOString(),
          suggested_field_keys: Object.keys(fieldsToApply),
        },
        // Mark the explicitly selected slug in event_match
        event_match: { ...(existing?.event_match || {}), selected_match_slug: slug },
      });

      navigate('/build-my-event');
    } catch (err) {
      console.error('EXP-01E: handlePlanLikeThis failed — navigating to BME without context', err);
      navigate('/build-my-event');
    } finally {
      setPlanningInspiration(false);
    }
  }, [navigate]);

  const bg = STEP_BG[step] || PJ.ivory;

  return (
    <>
      <style>{findStyles}</style>
      <div data-testid="find-events-page" style={{ minHeight: "100vh", backgroundColor: bg, display: "flex", flexDirection: "column", transition: "background-color 0.25s ease" }}>
        <FindNav />
        <ProgressDots step={step} />

        {step === 1 && <Step1 onSelect={handleTypeSelect} />}
        {step === 2 && (
          <Step2
            answers={answers}
            setAnswers={setAnswers}
            onBack={() => setSearchParams(prev => {
              const next = new URLSearchParams(prev);
              next.set("step", "1");
              return next;
            })}
            onSubmit={handleSubmit}
          />
        )}
        {step === 3 && (
          loading
            ? <FindLoading />
            : results !== null && (
              <Step3
                answers={answers}
                results={results}
                onRestart={handleRestart}
                onViewStory={handleViewStory}
                onPlanLikeThis={handlePlanLikeThis}
                isPlanning={planningInspiration}
                showMore={showMore}
                setShowMore={setShowMore}
              />
            )
        )}
      </div>
    </>
  );
}
